import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import './App.css';
import menuData from './data.json';

// Görselleri import ediyoruz
import logo from './assets/kanatci-ayaz-logo.png';
import kanatTablaci from './assets/kanat-tablaci.jpg';
import kanatPatates from './assets/kanat-patates.jpg';
import patates from './assets/patates.jpg';
import placeholder from './assets/placeholder.jpg';
import istanbul from './assets/istanbul.jpeg';
import ankara from './assets/ankara.jpeg';

// Görsel mapping objesi
const imageMap = {
  'kanat-tablaci.jpg': kanatTablaci,
  'kanat-patates.jpg': kanatPatates,
  'patates.jpg': patates,
  'placeholder.jpg': placeholder,
  'istanbul.jpeg': istanbul,
  'ankara.jpeg': ankara
};

// Erişilebilirlik Context
const AccessibilityContext = createContext();

export function useAccessibility() {
  return useContext(AccessibilityContext);
}

function AccessibilityProvider({ children }) {
  const [fontSize, setFontSize] = useState(100);
  const [contrast, setContrast] = useState('normal');
  const [reduceMotion, setReduceMotion] = useState(false);
  const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);

  // Font boyutunu değiştir
  const changeFontSize = (direction) => {
    setFontSize(prev => {
      const newSize = direction === 'increase' ? prev + 10 : prev - 10;
      return Math.min(Math.max(newSize, 80), 150); // 80% ile 150% arası
    });
  };

  // Kontrastı değiştir
  const toggleContrast = () => {
    setContrast(prev => prev === 'normal' ? 'high' : 'normal');
  };

  // Hareketi azalt
  const toggleReduceMotion = () => {
    setReduceMotion(prev => !prev);
  };

  // Erişilebilirlik panelini aç/kapat
  const toggleAccessibilityPanel = () => {
    setShowAccessibilityPanel(prev => !prev);
  };

  useEffect(() => {
    // Font boyutunu uygula
    document.documentElement.style.fontSize = `${fontSize}%`;

    // Kontrastı uygula
    document.body.classList.toggle('high-contrast', contrast === 'high');

    // Hareket azaltmayı uygula
    document.body.classList.toggle('reduce-motion', reduceMotion);
  }, [fontSize, contrast, reduceMotion]);

  return (
    <AccessibilityContext.Provider 
      value={{
        fontSize,
        contrast,
        reduceMotion,
        showAccessibilityPanel,
        changeFontSize,
        toggleContrast,
        toggleReduceMotion,
        toggleAccessibilityPanel
      }}
    >
      {children}
      <AccessibilityControls />
    </AccessibilityContext.Provider>
  );
}

function AccessibilityControls() {
  const {
    fontSize,
    contrast,
    reduceMotion,
    showAccessibilityPanel,
    changeFontSize,
    toggleContrast,
    toggleReduceMotion,
    toggleAccessibilityPanel
  } = useAccessibility();

  // Dışarı tıklamayı yönet
  const panelRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (showAccessibilityPanel &&
          panelRef.current && 
          !panelRef.current.contains(event.target) &&
          !buttonRef.current.contains(event.target)) {
        toggleAccessibilityPanel();
      }
    }

    // ESC tuşu ile kapatma
    function handleEscKey(event) {
      if (showAccessibilityPanel && event.key === 'Escape') {
        toggleAccessibilityPanel();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showAccessibilityPanel, toggleAccessibilityPanel]);

  return (
    <>
      <button
        ref={buttonRef}
        className={`accessibility-toggle ${showAccessibilityPanel ? 'active' : ''}`}
        onClick={toggleAccessibilityPanel}
        aria-expanded={showAccessibilityPanel}
        aria-label="Erişilebilirlik seçeneklerini aç/kapat"
      >
        <span className="accessibility-icon" aria-hidden="true">♿</span>
      </button>

      {showAccessibilityPanel && (
        <>
          <div className="accessibility-overlay" onClick={toggleAccessibilityPanel} />
          <div 
            ref={panelRef}
            className="accessibility-panel"
            role="dialog"
            aria-label="Erişilebilirlik Seçenekleri"
          >
            <div className="accessibility-header">
              <h2>Erişilebilirlik Seçenekleri</h2>
              <button
                onClick={toggleAccessibilityPanel}
                aria-label="Erişilebilirlik panelini kapat"
                className="close-button"
              >
                ✕
              </button>
            </div>

            <div className="accessibility-controls">
              <div className="control-group">
                <h3>Yazı Boyutu</h3>
                <div className="button-group">
                  <button
                    onClick={() => changeFontSize('decrease')}
                    aria-label="Yazı boyutunu küçült"
                    disabled={fontSize <= 80}
                  >
                    A-
                  </button>
                  <span className="font-size-value">{fontSize}%</span>
                  <button
                    onClick={() => changeFontSize('increase')}
                    aria-label="Yazı boyutunu büyüt"
                    disabled={fontSize >= 150}
                  >
                    A+
                  </button>
                </div>
              </div>

              <div className="control-group">
                <h3>Kontrast</h3>
                <button
                  onClick={toggleContrast}
                  aria-pressed={contrast === 'high'}
                  className={contrast === 'high' ? 'active' : ''}
                >
                  Yüksek Kontrast {contrast === 'high' ? '(Açık)' : '(Kapalı)'}
                </button>
              </div>

              <div className="control-group">
                <h3>Hareket</h3>
                <button
                  onClick={toggleReduceMotion}
                  aria-pressed={reduceMotion}
                  className={reduceMotion ? 'active' : ''}
                >
                  Azaltılmış Hareket {reduceMotion ? '(Açık)' : '(Kapalı)'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function MenuPage() {
  const { city } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('lezzetler');
  const [isLoading, setIsLoading] = useState(false);
  const [animateItems, setAnimateItems] = useState(false);

  useEffect(() => {
    if (!['istanbul', 'ankara'].includes(city)) {
      navigate('/');
    } else {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setTimeout(() => setAnimateItems(true), 100);
      }, 500);
    }
  }, [city, navigate]);

  const handleCategoryChange = (category) => {
    setAnimateItems(false);
    setTimeout(() => {
      setSelectedCategory(category);
      setAnimateItems(true);
    }, 300);
  };

  const handleKeyPress = (e, category) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCategoryChange(category);
    }
  };

  if (!city) return null;

  const cityMenu = menuData[city] || { lezzetler: [], icecekler: [] };
  const cityName = city === 'istanbul' ? 'İstanbul' : city.charAt(0).toUpperCase() + city.slice(1);

  return (
    <main className={`menu-container ${isLoading ? 'loading' : ''}`} role="main">
      <div className="content-wrapper">
        {isLoading ? (
          <div className="loading-screen" role="alert" aria-live="polite">
            <div className="spinner" role="progressbar" aria-valuetext="Yükleniyor"></div>
            <p>Menü yükleniyor...</p>
          </div>
        ) : (
          <div className="menu-content">
            <div className="header-section">
              <div className="logo-container">
                <img src={logo} alt="Kanatçı Ayaz Logo" className="restaurant-logo" />
              </div>

              <nav className="category-buttons" role="navigation" aria-label="Menü kategorileri">
                <button 
                  className={selectedCategory === 'lezzetler' ? 'active' : ''}
                  onClick={() => handleCategoryChange('lezzetler')}
                  onKeyPress={(e) => handleKeyPress(e, 'lezzetler')}
                  aria-pressed={selectedCategory === 'lezzetler'}
                  aria-label="Lezzetler kategorisi"
                >
                  Lezzetler
                </button>
                <button 
                  className={selectedCategory === 'icecekler' ? 'active' : ''}
                  onClick={() => handleCategoryChange('icecekler')}
                  onKeyPress={(e) => handleKeyPress(e, 'icecekler')}
                  aria-pressed={selectedCategory === 'icecekler'}
                  aria-label="İçecekler kategorisi"
                >
                  İçecekler
                </button>
              </nav>
            </div>

            <section 
              className={`menu-items ${animateItems ? 'animate' : ''}`}
              aria-label={`${cityName} ${selectedCategory} menüsü`}
            >
              {(cityMenu[selectedCategory] || []).map((item, index) => (
                <article key={index} className="menu-item">
                  {item.gorsel && (
                    <div className="menu-item-image-container">
                      <img 
                        src={imageMap[item.gorsel] || `${process.env.PUBLIC_URL}/assets/${item.gorsel}`}
                        alt={`${item.isim} görseli`}
                        className="menu-item-image"
                        loading="lazy"
                        onError={(e) => {
                          console.error(`Görsel yüklenemedi: ${item.gorsel}`);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="menu-item-details">
                    <div className="menu-item-header">
                      <h2>{item.isim}</h2>
                      <div className="price-tag" aria-label={`Fiyat: ${item.fiyat} TL`}>
                        <span className="price">{item.fiyat.toLocaleString('tr-TR')}</span>
                        <span className="currency">₺</span>
                      </div>
                    </div>
                    {item.aciklama && <p className="description">{item.aciklama}</p>}
                    {item.buyuk_porsiyon && (
                      <div className="portion-options" role="group" aria-label="Porsiyon seçenekleri">
                        <div className="portion-option">
                          <span className="portion-name">Tek Porsiyon</span>
                          <div className="price-tag" aria-label={`Tek porsiyon fiyatı: ${item.fiyat} TL`}>
                            <span className="price">{item.fiyat.toLocaleString('tr-TR')}</span>
                            <span className="currency">₺</span>
                          </div>
                        </div>
                        <div className="portion-option">
                          <span className="portion-name">Bir Buçuk Porsiyon</span>
                          <div className="price-tag" aria-label={`Bir Buçuk porsiyon fiyatı: ${item.buyuk_porsiyon.fiyat} TL`}>
                            <span className="price">{item.buyuk_porsiyon.fiyat.toLocaleString('tr-TR')}</span>
                            <span className="currency">₺</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </section>

            <div className="navigation-footer">
              <button 
                className="back-button"
                onClick={() => {
                  setAnimateItems(false);
                  setTimeout(() => navigate('/'), 300);
                }}
                aria-label="Şehir seçimine geri dön"
              >
                Şehir Seçimine Geri Dön
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function CitySelection() {
  const navigate = useNavigate();

  const handleCitySelect = (city) => {
    if (city === 'amsterdam') return;
    navigate(`/menu/${city}`);
  };

  const handleKeyPress = (e, city) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCitySelect(city);
    }
  };

  return (
    <main className="city-selection" role="main">
      <div className="city-content-wrapper">
        <div 
          className="logo-container"
          onClick={() => navigate('/menu/istanbul')}
          onKeyPress={(e) => handleKeyPress(e, 'istanbul')}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          aria-label="İstanbul menüsüne git"
        >
          <img src={logo} alt="Kanatçı Ayaz Logo" className="restaurant-logo" />
        </div>
        <h1>QR Menü</h1>
        <h2 id="city-selection-heading">Şehir Seçiniz</h2>
        <div 
          className="city-buttons" 
          role="group" 
          aria-labelledby="city-selection-heading"
        >
          {Object.entries(menuData).map(([cityKey, cityData]) => (
            <button 
              key={cityKey}
              onClick={() => handleCitySelect(cityKey)}
              onKeyPress={(e) => handleKeyPress(e, cityKey)}
              className="city-card"
              aria-label={`${cityKey === 'istanbul' ? 'İstanbul' : cityKey.charAt(0).toUpperCase() + cityKey.slice(1)} şubesini seç`}
            >
              <div 
                className="city-image" 
                style={{
                  backgroundImage: `url(${imageMap[cityData.sehir_gorsel]})`
                }}
                role="img"
                aria-label={`${cityKey === 'istanbul' ? 'İstanbul' : cityKey.charAt(0).toUpperCase() + cityKey.slice(1)} şubesi görseli`}
              ></div>
              <h3>{cityKey === 'istanbul' ? 'İstanbul' : cityKey.charAt(0).toUpperCase() + cityKey.slice(1)}</h3>
            </button>
          ))}
          <button 
            className="city-card coming-soon" 
            disabled
            aria-label="Amsterdam şubesi yakında açılacak"
          >
            <div 
              className="city-image" 
              style={{ backgroundColor: '#f5f5f5' }}
            >
              <div className="coming-soon-overlay">
                <span>Çok Yakında</span>
              </div>
            </div>
            <h3>Amsterdam</h3>
          </button>
        </div>
      </div>
    </main>
  );
}

function App() {
  return (
    <AccessibilityProvider>
      <Router>
        <a href="#main-content" className="skip-to-main">
          Ana içeriğe git
        </a>
        <Routes>
          <Route path="/" element={<CitySelection />} />
          <Route path="/menu/:city" element={<MenuPage />} />
        </Routes>
      </Router>
    </AccessibilityProvider>
  );
}

export default App;
